import { useCallback, useEffect, useRef, useState } from "react";
import type { SettingsResponse } from "../../shared/types";
import { useToast } from "../components/ui/Toast";
import { ApiError, api } from "../lib/api";

const RECHECK_SECONDS = 12;

/**
 * Checking the records: Check now, then again every 12 seconds, one check at a time. It stops when the screen is
 * left or the address changes, and a check that comes back after either is dropped, so it never paints over the
 * address that replaced it (specs/formal/domain-verify).
 */
export function useRecordCheck(domain: string | null, verified: boolean, setData: (d: SettingsResponse) => void) {
  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState(0);
  const [left, setLeft] = useState(RECHECK_SECONDS);
  const busy = useRef(false);
  const alive = useRef(true);
  const toast = useToast();

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const check = useCallback(async () => {
    if (busy.current || !domain) return;
    busy.current = true;
    setChecking(true);
    try {
      const d = await api.settings.verifySending();
      if (!alive.current || d.sending.domain !== domain) return;
      setData(d);
      if (d.sending.status === "verified") {
        toast(`All ${d.sending.records.length} records found. Your email is verified.`);
      }
    } catch (e) {
      if (alive.current) toast({ text: e instanceof ApiError ? e.message : "We couldn’t check just now. Try again.", icon: "info" });
    } finally {
      busy.current = false;
      if (alive.current) {
        setChecking(false);
        setChecks((n) => n + 1);
        setLeft(RECHECK_SECONDS);
      }
    }
  }, [domain, setData, toast]);

  useEffect(() => {
    if (checks === 0 || checking || verified) return;
    const t = window.setInterval(() => setLeft((n) => n - 1), 1000);
    return () => window.clearInterval(t);
  }, [checks, checking, verified]);

  useEffect(() => {
    if (left <= 0 && !busy.current && !verified) void check();
  }, [left, verified, check]);

  return { checking, checks, left, check };
}
